// ─────────────────────────────────────────────────────────────────────────────
// src/app/reducers.ts  — ROOT NgRx store registration
//
// THIS is the file imported by app.config.ts (provideStore).
// It must include EVERY reducer that needs to be available at app startup.
//
// Feature stores that are lazy-loaded via provideState() in their route files:
//   ✅ stock-transfer     → routes.ts
//   ✅ search-results     → routes.ts
//   ✅ consume            → routes.ts (also added here because TokenHolderService
//                           and shared components dispatch consume actions globally)
//   ✅ organization       → routes.ts
// ─────────────────────────────────────────────────────────────────────────────

import {
  ActionReducer,
  ActionReducerMap,
  MetaReducer,
  INIT,
  UPDATE
} from '@ngrx/store';
import { isDevMode } from '@angular/core';

// Core store
import {
  appReducer,
  commonFeatureKey,
  AppState as CommonAppState
} from '@core/store/reducers/common.reducer';

// Stock-correction — effects registered in app.config.ts so reducer must be here
import {
  stockCorrectionReducer,
  stockCorrectionFeatureKey,
  StockCorrectionState
} from './features/stock-correction/stores/stock-correction.reducer';

// Dashboard — used by dashboard.ts via store.select
import {
  dashboardFeatureKey,
  dashboardReducer,
  DashboardState
} from '@features/home/pages/dashboard/stores/dashboard.reducer';

// Consume — TokenHolderService and shared components dispatch consume actions
// from outside the lazy consume-stock route, so this must live in the root store
import {
  consumeFeatureKey,
  consumeReducer
} from './features/consume-stock/consume-store/consume.reducer';
import { ConsumeState } from './features/consume-stock/consume-store/consume.state';

// ── State interface ───────────────────────────────────────────────────────────
export interface State {
  [commonFeatureKey]: CommonAppState;
  [stockCorrectionFeatureKey]: StockCorrectionState;
  [dashboardFeatureKey]: DashboardState;
  [consumeFeatureKey]: ConsumeState;
}

// ── Root reducer map ──────────────────────────────────────────────────────────
export const reducers: ActionReducerMap<State> = {
  [commonFeatureKey]: appReducer,
  [stockCorrectionFeatureKey]: stockCorrectionReducer,
  [dashboardFeatureKey]: dashboardReducer,
  [consumeFeatureKey]: consumeReducer,
};

// ── Hydration meta-reducer ────────────────────────────────────────────────────
// Rehydrates the 'common' slice from sessionStorage on page refresh.
// Only the common slice is persisted (contains user config, privileges, etc.)
export const hydrationMetaReducer = (
  reducer: ActionReducer<any>
): ActionReducer<any> => {
  return (state, action) => {
    if (action.type === INIT || action.type === UPDATE) {
      const storageValue = sessionStorage.getItem('state');
      if (storageValue) {
        try {
          return JSON.parse(storageValue);
        } catch {
          sessionStorage.removeItem('state');
        }
      }
    }
    const nextState = reducer(state, action);
    // Only persist the common slice — feature slices are ephemeral
    sessionStorage.setItem(
      'state',
      JSON.stringify({ [commonFeatureKey]: nextState[commonFeatureKey] })
    );
    return nextState;
  };
};

// Hydration active in both dev and prod — matches V13 behaviour
export const metaReducers: MetaReducer<State>[] = [hydrationMetaReducer];